import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { z } from 'zod'

dayjs.extend(customParseFormat)

// URL のクエリは利用者が書き換えられる。絞り込み条件に入る日付がそのまま GraphQL へ渡ると、
// サーバーが不正な日付でエラーを返し、一覧が取得エラー表示に置き換わって条件を直せなくなる。
// そのため実在する日付だけを通す

/** 'YYYY-MM-DD' 形式の実在する日付か（2026-02-30 のような存在しない日付は false） */
export function isRealDateString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value, 'YYYY-MM-DD', true).isValid()
}

/** DateRangeField が出す 'YYYY-MM-DDT00:00:00.000Z' 形式（または日付だけ）の実在する日付か */
export function isRealDateTimeString(value: string): boolean {
  const date = value.slice(0, 10)
  const rest = value.slice(10)
  return isRealDateString(date) && (rest === '' || rest === 'T00:00:00.000Z')
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const hasInvalidDateRange = (value: unknown): boolean => {
  if (!isRecord(value)) {
    return false
  }
  return [value.gte, value.lte].some((bound) => typeof bound === 'string' && !isRealDateTimeString(bound))
}

/**
 * 絞り込み条件のオブジェクトを、キー単位で検証して読み込む。
 * 型の合わないキー・実在しない日付を含む範囲はそのキーだけ捨て、他の正しい条件は残す
 * （1 つ壊れただけで全条件が消えると、共有された URL が使えなくなるため）。未知のキーも捨てる
 */
export function parseFilterByKey<S extends z.ZodObject>(schema: S, raw: unknown): z.infer<S> {
  const source = isRecord(raw) ? raw : {}
  const candidate: Record<string, unknown> = {}
  const shape: Readonly<Record<string, z.ZodType>> = schema.shape
  for (const [key, valueSchema] of Object.entries(shape)) {
    const value = source[key]
    if (value === undefined || hasInvalidDateRange(value)) {
      continue
    }
    const result = valueSchema.safeParse(value)
    if (result.success) {
      candidate[key] = result.data
    }
  }
  return schema.parse(candidate)
}
